// src/components/DataTable.js
import React, { useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { Table, Form, Button, Pagination } from 'react-bootstrap';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
const normalizeSearchText = (value) => {
  return String(value || '')
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/([a-z])([A-Z])/g, '$1$2');
};

const DataTable = ({ 
  data, 
  columns, 
  renderMobileItem, 
  searchPlaceholder = "Search..." 
}) => {
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const table = useReactTable({
    data,
    columns,
    state: {
      globalFilter,
      sorting,
      pagination,
    },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    // getCoreRowModel: getCoreRowModel(),
    // getFilteredRowModel: getFilteredRowModel(),
    // getSortedRowModel: getSortedRowModel(),
    // getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: (row, columnId, filterValue) => {
    const cellValue = row.getValue(columnId);
      return normalizeSearchText(cellValue).includes(
        normalizeSearchText(filterValue)
      );
    },

    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div className="data-table-container">
      {/* Global Filter Search */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Form.Control
          type="text"
          placeholder={searchPlaceholder}
          value={globalFilter ?? ''}
          onChange={(e) => setGlobalFilter(e.target.value)}
           className="bg-dark text-white border-secondary search-input"
          // className="bg-dark text-white border-secondary"
          // style={{ maxWidth: '300px' }}
        />
        
        {/* Page Size Selector */}
        <Form.Select
          value={table.getState().pagination.pageSize}
          onChange={e => {
            table.setPageSize(Number(e.target.value))
          }}
          className="bg-dark text-white border-secondary w-auto"
        >
          {[10, 20, 30, 50].map(pageSize => (
            <option key={pageSize} value={pageSize}>
              Show {pageSize}
            </option>
          ))}
        </Form.Select>
      </div>

      {/* DESKTOP TABLE */}
      <div className="d-none d-md-block table-responsive shadow-lg rounded overflow-hidden">
        <Table hover className="mb-0 custom-dark-table align-middle">
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th 
                    key={header.id} 
                    onClick={header.column.getToggleSortingHandler()}
                    style={{ cursor: header.column.getCanSort() ? 'pointer' : 'default' }}
                    className="text-white"
                  >
                    <div className="d-flex align-items-center gap-2">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {/* Sorting Icons */}
                      {{
                        asc: <FaSortUp className="text-info" />,
                        desc: <FaSortDown className="text-info" />,
                      }[header.column.getIsSorted()] ?? (
                        header.column.getCanSort() ? <FaSort className="opacity-25" /> : null
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map(row => (
                <tr key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="text-center py-4 text-white opacity-50">
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* MOBILE VIEW (Renders using the passed prop function) */}
      <div className="d-md-none">
        {table.getRowModel().rows.length > 0 ? (
          table.getRowModel().rows.map(row => renderMobileItem(row.original))
        ) : (
          <div className="text-center py-4 text-white opacity-50">
            No records found
          </div>
        )}
      </div>

      {/* PAGINATION CONTROLS */}
      {table.getPageCount() > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
          <span className="text-white opacity-75 small">
            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
          </span>
          <Pagination className="mb-0 custom-pagination" size="sm">
            <Pagination.First onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()} />
            <Pagination.Prev onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()} />
            <Pagination.Next onClick={() => table.nextPage()} disabled={!table.getCanNextPage()} />
            <Pagination.Last onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()} />
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default DataTable;